/**
 * Frontend Security Utilities
 * Provides XSS protection, input sanitization, and CSRF token management
 */

import DOMPurify from 'dompurify';

// ============== XSS PROTECTION ==============

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirty - Potentially unsafe HTML string
 * @param {object} options - DOMPurify configuration options
 * @returns {string} Sanitized HTML string
 */
export const sanitizeHTML = (dirty, options = {}) => {
    if (!dirty) return '';
    
    const defaultOptions = {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        ALLOW_DATA_ATTR: false,
    };
    
    return DOMPurify.sanitize(dirty, { ...defaultOptions, ...options });
};

/**
 * Sanitize plain text - strips all HTML tags
 * @param {string} dirty - Potentially unsafe string
 * @returns {string} Plain text string
 */
export const sanitizeText = (dirty) => {
    if (!dirty) return '';
    return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Sanitize user input for form fields
 * Removes potentially dangerous characters while preserving normal text
 * @param {string} input - User input string
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input, maxLength = 500) => {
    if (!input) return '';
    
    // Strip HTML
    let sanitized = DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    
    // Remove null bytes and control characters (except newlines and tabs)
    sanitized = sanitized.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, '');
    
    // Limit length
    return sanitized.slice(0, maxLength).trim();
};

/**
 * Escape HTML entities for safe display
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export const escapeHTML = (str) => {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
};

// ============== CSRF PROTECTION ==============

let csrfToken = null;

/**
 * Set CSRF token (called after login)
 * @param {string} token - CSRF token from server
 */
export const setCsrfToken = (token) => {
    csrfToken = token;
};

/**
 * Get current CSRF token
 * @returns {string|null} Current CSRF token
 */
export const getCsrfToken = () => csrfToken;

/**
 * Clear CSRF token (called on logout)
 */
export const clearCsrfToken = () => {
    csrfToken = null;
};

/**
 * Get headers with CSRF token for API requests
 * @param {object} additionalHeaders - Additional headers to include
 * @returns {object} Headers object with CSRF token
 */
export const getSecureHeaders = (additionalHeaders = {}) => {
    const headers = {
        'Content-Type': 'application/json',
        ...additionalHeaders,
    };
    
    if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
    }
    
    return headers;
};

// ============== URL VALIDATION ==============

/**
 * Validate and sanitize URLs to prevent javascript: and data: attacks
 * @param {string} url - URL to validate
 * @param {array} allowedProtocols - Allowed protocols
 * @returns {string|null} Sanitized URL or null if invalid
 */
export const sanitizeURL = (url, allowedProtocols = ['http:', 'https:', 'mailto:']) => {
    if (!url) return null;
    
    try {
        const parsed = new URL(url, window.location.origin);
        
        if (!allowedProtocols.includes(parsed.protocol)) {
            console.warn('Blocked potentially dangerous URL:', url);
            return null;
        }
        
        return parsed.href;
    } catch {
        // Relative URL or invalid - check for javascript:
        if (url.toLowerCase().startsWith('javascript:') || url.toLowerCase().startsWith('data:')) {
            console.warn('Blocked dangerous URL scheme:', url);
            return null;
        }
        return url;
    }
};

// ============== INPUT VALIDATION ==============

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmail = (email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validate username format (alphanumeric, underscore, hyphen, dot)
 * @param {string} username - Username to validate
 * @returns {boolean} True if valid username format
 */
export const isValidUsername = (username) => {
    if (!username) return false;
    const usernameRegex = /^[a-zA-Z0-9_\-.]+$/;
    return usernameRegex.test(username) && username.length >= 3 && username.length <= 50;
};

/**
 * Validate phone number format (basic validation)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid phone format
 */
export const isValidPhone = (phone) => {
    if (!phone) return true; // Phone is optional
    const phoneRegex = /^[\d\s\-+()]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 8;
};

// ============== SECURE STORAGE ==============

/**
 * Securely store non-sensitive data in sessionStorage
 * Note: Never store tokens or sensitive data client-side
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 */
export const secureSessionSet = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.warn('sessionStorage not available');
    }
};

/**
 * Retrieve data from sessionStorage
 * @param {string} key - Storage key
 * @returns {any} Stored value or null
 */
export const secureSessionGet = (key) => {
    try {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch {
        return null;
    }
};

/**
 * Remove data from sessionStorage
 * @param {string} key - Storage key
 */
export const secureSessionRemove = (key) => {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // Ignore errors
    }
};

export default {
    sanitizeHTML,
    sanitizeText,
    sanitizeInput,
    escapeHTML,
    setCsrfToken,
    getCsrfToken,
    clearCsrfToken,
    getSecureHeaders,
    sanitizeURL,
    isValidEmail,
    isValidUsername,
    isValidPhone,
    secureSessionSet,
    secureSessionGet,
    secureSessionRemove,
};
